<template>
  <ChartCard v-if="!pageLoading">
    <ChartHeader label="Job Posts" :tooltip-key="tooltipKey" tooltip-placement="bottom" />
    <div class="mt-4">
      <div class="posts-list-header">
        <div class="posts-list-inner-header pb-2">
          <span class="list-score">Score</span>
          <span class="list-title">Title</span>
          <span class="list-created">Created</span>
          <span class="list-days-live">Days Live</span>
          <span class="list-status">Status</span>
        </div>
      </div>
      <div class="posts-list-container">
        <ul class="posts-list" :class="postsTotalPages > 1 ? 'pagination' : ''">
          <NoEditorWrapper
            v-for="{
              ats_post_id: id,
              document_id: documentId,
              post_title: title,
              location_name: location,
              days_live: daysLive,
              created_at: createdAt,
              is_deleted: isDeleted,
              status,
              score,
              is_internal: isInternal,
              is_evergreen: isEvergreen,
            } in posts"
            :key="id"
            :redirect-route="documentId ? { query: { preview: documentId, type: 'document' } } : null"
          >
            <DpplListItem
              :to="documentId && !isDeleted ? { name: 'editor-view', params: { id: documentId } } : undefined"
              :class="{ disabled: !documentId || isDeleted }"
              :ripple="documentId && !isDeleted"
              @click.stop.prevent="handleListItemClick(documentId, documentId && !isDeleted)"
            >
              <template #left>
                <div class="left-column">
                  <DocumentScoreLink
                    v-if="formatScore(score, isDeleted, documentId) !== '-'"
                    :id="documentId"
                    :score="formatScore(score, isDeleted, documentId)"
                    class="mr-5"
                  />
                  <DpplTooltip
                    v-else
                    teleport
                  >
                    <template #activator="{ onMouseenter, onMouseleave }">
                      <div
                        class="d-flex score-tooltip"
                        @mouseenter="onMouseenter"
                        @mouseleave="onMouseleave"
                      >
                        <ScoreMeter :score="formatScore(score, isDeleted, documentId)" icon animate class="mr-5" />
                      </div>
                    </template>
                    <template #tooltip-content>
                      <span>Unavailable to edit in JobNav</span>
                    </template>
                  </DpplTooltip>
                  <div class="left-column-inner">
                    <div class="job-title mb-1">
                      <h5>{{ title || 'No Title Found' }}</h5>
                    </div>
                    <div class="company-and-location">
                      <span v-if="company">{{ company }}</span>
                      <span v-if="company && location" class="mx-1">-</span>
                      <span v-if="location">{{ location }}</span>
                    </div>
                  </div>
                </div>
              </template>
              <template #right>
                <JobBadges
                  v-if="isInternal || isEvergreen"
                  :is-internal="isInternal"
                  :is-evergreen="isEvergreen"
                  class="mr-4"
                />
                <div class="right-column">
                  <div class="created-date">
                    <span>{{ formatDate(createdAt) || 'N/A' }}</span>
                  </div>
                </div>
                <div class="right-column">
                  <div
                    class="days-live"
                    :class="{
                      unpublished: isUnpublishedClass(status, documentId, isDeleted, daysLive),
                      'unpublished-na': daysLive === 'N/A' && status === 'unpublished' && documentId && !isDeleted,
                    }"
                  >
                    <h5>
                      {{ getDaysLiveText(daysLive, status, documentId, isDeleted) }}
                    </h5>
                  </div>
                </div>
                <div class="right-column">
                  <div class="status">
                    <h5>{{ status ? startCase(status) : '-' }}</h5>
                  </div>
                </div>
              </template>
            </DpplListItem>
          </NoEditorWrapper>
        </ul>
      </div>
      <div class="footer-container">
        <Pagination
          v-if="postsTotalPages > 1"
          class="mt-3 mr-4 pagination"
          :model-value="postsPage"
          :length="postsTotalPages"
          @update:model-value="(value) => updatePagination(value)"
        />
      </div>
    </div>
  </ChartCard>
  <div v-else class="loader">
    <WidgetHeaderSkeleton class="mb-6" />
    <DocumentListLoader :is-loading="pageLoading" :number-to-load="5" :style="{ width: '100%' }" />
  </div>
</template>

<script>
import { useRequisitionReport } from '@/stores/requisitions/requisitionReport';
import { mapActions, mapState } from 'pinia';
import DpplListItem from '@/components/datapeople/DpplListItem.vue';
import DpplTooltip from '@/components/datapeople/DpplTooltip.vue';
import NoEditorWrapper from '@/components/editor/NoEditorWrapper.vue';
import { useAppLoading } from '@/stores/app/loading';
import DocumentListLoader from '@/components/library/DocumentListLoader.vue';
import WidgetHeaderSkeleton from '@/components/insights/skeletonLoaders/WidgetHeaderSkeleton.vue';
import Pagination from '@/components/shared/Pagination.vue';
import { useAppAuthData } from '@/stores/app/authData';
import { useTracking } from '@/stores/tracking/tracking';
import { format } from 'date-fns';
import { useTooltips } from '@/stores/insights/tooltips';
import { startCase } from 'lodash-es';
import ScoreMeter from '@/components/editor/ScoreMeter.vue';
import JobBadges from '@/components/editor/badge/JobBadges.vue';
import ChartCard from '@/components/insights/Chart/ChartCard.vue';
import ChartHeader from '@/components/insights/Chart/ChartHeader.vue';
import DocumentScoreLink from '@/components/shared/DocumentScoreLink.vue';

export default {
  name: 'RequisitionReportPosts',
  components: {
    DpplListItem,
    DocumentListLoader,
    NoEditorWrapper,
    WidgetHeaderSkeleton,
    Pagination,
    ChartCard,
    ChartHeader,
    ScoreMeter,
    DpplTooltip,
    JobBadges,
    DocumentScoreLink,
  },
  data() {
    return {
      tooltipKey: 'single-req-job-posts',
    };
  },
  computed: {
    ...mapState(useTooltips, ['tooltips']),
    ...mapState(useRequisitionReport, ['posts', 'postsTotalPages', 'postsPage']),
    ...mapState(useAppLoading, ['pageLoading']),
    ...mapState(useAppAuthData, ['authData']),
    tooltip() {
      const { body_r: html, mode } = this.tooltips?.[this.tooltipKey] || {};

      return {
        html,
        mode,
        key: this.tooltipKey,
      };
    },
    company() {
      return this.authData.companyName;
    },
  },
  methods: {
    ...mapActions(useRequisitionReport, ['updatePostsPagination']),
    getDaysLiveText(daysLive, status, documentId, isDeleted) {
      const fallback = status === 'unpublished' && documentId && !isDeleted ? 'N/A' : '-';

      return (typeof daysLive === 'number' && status !== 'draft')
        || (status === 'draft' && daysLive)
        ? daysLive
        : fallback;
    },
    isUnpublishedClass(status, documentId, isDeleted, daysLive) {
      return status === 'unpublished'
        && (!(!documentId || isDeleted) || typeof daysLive !== 'number');
    },
    startCase(value) {
      return startCase(value);
    },
    formatScore(score, isDeleted, documentId) {
      if (isDeleted || !documentId) return '-';
      return typeof score === 'number' && score >= 0 ? score : 0;
    },
    async updatePagination(value) {
      await this.updatePostsPagination(value);
    },
    handleListItemClick(id, shouldNavigate) {
      if (shouldNavigate) {
        this.$router.replace({ query: { ...this.$route.query, preview: id, type: 'document' } });
      }

      useTracking().requisitionJobPostClicked(id);
    },
    formatDate(date) {
      return format(new Date(date), 'MM/dd/yy');
    },
  },
};
</script>

<style lang="less" scoped>
@import '@/less/mixins';

.loader {
  height: 459px;
  border-radius: 10px;
  flex: 2;
  background: @white;
  padding: 24px;
  .shadow();

  :deep(.v-skeleton-loader__text) {
    margin: 0px;
  }
}

.posts-header {
  height: 68px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 0px 24px;

  h4 {
    font-size: 16px;
    font-weight: 700;
    line-height: 20px;
  }
}

.posts-list-container {
  width: 100%;
  padding: 0px 24px;
}

.posts-list {
  height: 341px;
  max-height: 341px;
  width: 100%;
  overflow-y: auto;
  border-radius: 0px 0px 10px 10px;

  &.pagination {
    height: 317px;
    max-height: 317px;
  }
}

.left-column {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 0px 24px 0px 0px;
}

.left-column-inner {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
}

.job-title h5 {
  font-size: 16px;
  font-weight: 500;
  line-height: 20px;
  max-width: 470px;
  text-align: left;
}

.company-and-location {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  text-align: left;

  span {
    font-size: 12px;
    font-weight: 300;
    line-height: 16px;
    color: @text-darkest;
  }
}

.right-column {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0px 24px;
  border-left: 2px solid @stroke-default;
}

.created-date {
  width: 59px;

  span {
    font-size: 12px;
    font-weight: 300;
    line-height: 16px;
    color: @text-darkest;
  }
}

.days-live {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  width: 52px;

  h5 {
    font-size: 16px;
    font-weight: 500;
    line-height: 20px;
    color: @text-brand;
  }

  &.unpublished h5 {
    color: @indigo-300;
  }

  &.unpublished-na h5 {
    font-size: 14px;
  }
}

.status {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  width: 90px;

  h5 {
    font-size: 12px;
    font-weight: 300;
    line-height: 16px;
  }
}

.footer-container {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-end;

  .pagination {
    width: 50%;
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }

  :deep(.v-pagination__list) {
    justify-content: flex-end !important;
  }
}

.dppl-list-item.disabled {
  cursor: default;

  &:hover {
    background: @white;
    box-shadow: none;
  }

  h5 {
    opacity: 0.6;
  }

  span {
    opacity: 0.6;
  }
}

.posts-list-header {
  width: 100%;
  padding: 0px 24px;
}

.posts-list-inner-header {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 2px solid @stroke-default;
}

.list-score {
  width: 60px;
  text-align: left;
  color: @text-darkest;
  font-size: 12px;
  line-height: 16px;
  font-weight: 700;
}

.list-title {
  width: calc(100% - 407px);
  text-align: left;
  color: @text-darkest;
  font-size: 12px;
  line-height: 16px;
  font-weight: 700;
}

.list-created {
  width: 109px;
  text-align: left;
  padding-left: 28px;
  color: @text-darkest;
  font-size: 12px;
  line-height: 16px;
  font-weight: 700;
}

.list-days-live {
  width: 100px;
  text-align: left;
  padding-left: 22px;
  color: @text-darkest;
  font-size: 12px;
  line-height: 16px;
  font-weight: 700;
}

.list-status {
  width: 140px;
  text-align: left;
  padding-left: 24px;
  color: @text-darkest;
  font-size: 12px;
  line-height: 16px;
  font-weight: 700;
}

.posts-header {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.score-tooltip {
  width: 40px;
  height: 40px;
}
</style>
